/**
 * Main front end point of entry.
 *
 * Copyright (C) 2018D Noom, Inc.
 * @author hubert
 */

import React from "react";
import { createRoot } from "react-dom/client";
import mixpanel from "mixpanel-browser";
import { MixpanelLogger } from "@noom/hive-analytics";
import { AuthConfiguration, AuthContext, CSExtras } from "@noom/hive-auth";

import App from "@app/App";
import { initDatadog, trackDatadogError } from "@app/datadog";
import {
  trackRumAuthReconcileClear,
  trackRumAuthRevalidateChanged,
} from "@app/rumMonitoring";
import { throwIfNullOrUndefined } from "@modules/common";
import {
  COACHSERVER_URL,
  IN_APP_LOGIN_KEY,
  NOOM_ENVIRONMENT,
} from "@modules/common/constants";
import { PatientProfileProvider } from "@modules/authentication/PatientProfileProvider";
import { setIdentityCookieFromActiveUser } from "@modules/authentication/IdentityCookie";
import { BrandNeutralLoading } from "common/NoomLoader";
import { installPrDeployRoutingAxiosInterceptor } from "@modules/dataFetching/prDeployRouting";

import { MixpanelTypes } from "./common/types";

installPrDeployRoutingAxiosInterceptor();

const mixpanelToken = throwIfNullOrUndefined(
  process.env.HIVE_MIXPANEL_TOKEN,
  "process.env.HIVE_MIXPANEL_TOKEN"
);

// Initialize mixpanel synchronously before React renders
mixpanel.init(mixpanelToken);

// Start Datadog RUM before anything else renders so the initial page load,
// its resources and any bootstrap error are all captured. No-ops unless the
// Datadog env vars are configured.
initDatadog();

// hive-auth bootstrap. hive-auth owns tokens/session; on a successful login
// (password or auto-cookie) we set the cross-app SSO cookie and log the
// analytics event. Patient metadata is fetched separately by
// PatientProfileProvider.
const csExtras: CSExtras = {
  packageName: "com.noom.web.healthcare",
};

// Lifecycle hooks into hive-auth's own store. These see mechanisms the app
// cannot: `onReconcileClear` in particular fires when hive-auth finds a stored
// token that disagrees with the source cookie and drops it, which is the most
// likely way a WebView user loses a session mid-use. Together with
// `auth.session_lost` from the login screen, one says a session was lost and
// the other says how.
const authObservers = {
  onReconcileClear: ({ cookieName }: { cookieName: string }) => {
    trackRumAuthReconcileClear({ cookieName });
  },
  onRevalidate: ({ changed }: { changed: boolean }) => {
    if (changed) {
      trackRumAuthRevalidateChanged();
    }
  },
  onStoreError: (err: unknown, context: string) => {
    trackDatadogError(err instanceof Error ? err : new Error(String(err)), {
      source: "hive-auth",
      context,
    });
  },
};

const authConfig: AuthConfiguration = {
  storage: "cookie",
  coachServer: {
    serverUrl: COACHSERVER_URL as string,
    clientId: "noom-coach",
    extras: csExtras,
  },
  autoCookie: {
    autoCookieName: IN_APP_LOGIN_KEY,
  },
  observers: authObservers,
};

if (process.env.HIVE_USE_MOCK_SERVER_API) {
  import("./mocks/mswBrowser").then(({ worker: mswWorker }) => {
    mswWorker.start();
  });
}

// Render the app
const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}

const root = createRoot(container);
root.render(
  <MixpanelLogger token={mixpanelToken} isDev={NOOM_ENVIRONMENT === "staging"}>
    <AuthContext
      config={authConfig}
      fallback={<BrandNeutralLoading />}
      onLoginSuccess={(user) => {
        // Preserve cross-app SSO (buyflow/userportal). `email` is the analytics
        // identity (distinct_id) and `access_code` an extra field — both are
        // available on the hive-auth profile at login, so we identify, register,
        // and emit `LoginSuccessful` immediately (no need to wait for the patient
        // profile). `user_id` is registered later as a super-property by
        // `MainLayout`/`usePatient` once the profile resolves.
        setIdentityCookieFromActiveUser(user);
        mixpanel.identify(user.email);
        mixpanel.register({ access_code: user.accessCode });
        mixpanel.track(MixpanelTypes.LoginSuccessful);
      }}
    >
      <PatientProfileProvider>
        <App />
      </PatientProfileProvider>
    </AuthContext>
  </MixpanelLogger>
);
